
import React, { useEffect, useState, useMemo } from 'react';
import { useAuthStore } from '../stores/useAuthStore';
import { usePieceStore } from '../stores/usePieceStore';
import { SampleRequestCard } from '../components/UI/SampleRequestCard';
import { BottomDock } from '../components/Layout/BottomDock';
import { Search, X, CheckCircle, Circle, Bell, ChevronRight, Clock, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DS } from '../theme/DesignSystem';
import { clsx } from 'clsx';
import { getDerivedStatus, isCuttingDone } from '../domain';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuthStore();
  const { samples, loading, error, fetchAssignedSamples, fetchRecentLogs, recentLogs } = usePieceStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    if (!userProfile?.factory_id) return;
    fetchAssignedSamples();
    fetchRecentLogs();
    
    // Only poll when online and page is visible
    const interval = setInterval(() => {
      if (navigator.onLine && document.visibilityState === 'visible') {
        fetchAssignedSamples();
        fetchRecentLogs();
      }
    }, 60000); // Increased to 60s to reduce load
    
    return () => clearInterval(interval);
  }, [userProfile?.factory_id]);

  // DERIVE RECENT ACTIVITY (Notifications)
  const notifications = useMemo(() => {
    if (recentLogs.length > 0) {
      return recentLogs.map(log => {
        // Find the sample this log belongs to (for style/buyer info)
        const matchedSample = samples.find(s =>
          s.sample_pieces?.some(p => p.id === log.sample_piece_id)
        );
        const styleName = matchedSample?.styles?.style_name || matchedSample?.styles?.style_code || '';
        const buyerName = matchedSample?.styles?.buyer_name || '';
        const sampleType = matchedSample?.sample_type || '';

        // Build descriptive message
        let action = '';
        switch (log.station) {
          case 'Cutting':
            action = log.activity === 'In' ? 'Cutting Started' : 'Cutting Completed';
            break;
          case 'Sewing':
            action = log.activity === 'In'
              ? `Assigned to ${(log as any).assigned_tailor_name || 'Tailor'}`
              : 'Sewing Completed';
            break;
          case 'Inline QC':
            action = log.qc_result === 'Pass' ? 'Inline QC Passed ✅' : 'Inline QC Failed ❌';
            break;
          case 'Endline QC':
          case 'Final QC':
            action = log.qc_result === 'Pass' ? 'Final QC Passed ✅' : 'Final QC Failed ❌';
            break;
          default:
            action = log.station + (log.activity ? ` ${log.activity}` : '');
        }

        const header = styleName
          ? `${styleName}${sampleType ? ` · ${sampleType}` : ''}`
          : 'Sample Update';
        const sub = buyerName ? `${buyerName} — ${action}` : action;

        return {
          id: log.id,
          msg: header,
          sub: sub,
          time: new Date(log.scanned_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          sampleId: matchedSample?.id || '',
        };
      });
    }

    // FALLBACK: Computed events if no logs
    const events: { id: string, msg: string, sub: string, time: string, sampleId: string }[] = [];
    const now = new Date();

    samples.forEach(s => {
      const prod = s.sample_production;
      const plan = s.sample_planning;
      if (!prod) return;
      const styleName = s.styles?.style_name || s.styles?.style_code || 'Sample';
      const buyerName = s.styles?.buyer_name || '';

      const addEvent = (dateStr: string | null | undefined, action: string) => {
        if (!dateStr) return;
        const date = new Date(dateStr);
        const diffHrs = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
        if (diffHrs < 24) {
          events.push({
            id: `${s.id}-${action}`,
            msg: styleName,
            sub: `${buyerName ? buyerName + ' — ' : ''}${action}`,
            time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            sampleId: s.id
          });
        }
      };

      addEvent(plan?.cutting_start_at, 'Cutting Started');
      addEvent(plan?.cutting_end_at, 'Cutting Completed');
      addEvent(plan?.sewing_start_at, 'Sewing Started');
      addEvent(plan?.sewing_end_at, 'Sewing Completed');
    });

    return events.sort((a, b) => new Date('1970/01/01 ' + b.time).getTime() - new Date('1970/01/01 ' + a.time).getTime()).slice(0, 10);
  }, [samples, recentLogs]);

  const filteredSamples = useMemo(() => {
    return samples.filter(sample => {
      const status = getDerivedStatus(sample);
      const prodStatus = sample.sample_production?.status || '';

      // Cutting Incharge Logic
      if (userProfile?.role === 'cutting_incharge' && !searchTerm) {
        // If they don't want to see completed stuff, and it's already past cutting, hide it
        if (!showCompleted && isCuttingDone(sample)) return false;

        // At the very beginning, if it's not even planned yet (File Received), hide it
        // Only show if it's Planned, Cutting Started, etc. 'Sample Production Planned' is the request status, production is 'Planned'
        if (!showCompleted && (prodStatus === 'File Received' || prodStatus === '')) return false;
      }

      // Other Roles Logic (Standard Completion)
      if (userProfile?.role !== 'cutting_incharge' && !searchTerm) {
        if (!showCompleted && status === 'Completed') return false;
      }

      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const matchesCode = sample.styles?.style_code?.toLowerCase().includes(lower);
        const matchesName = sample.styles?.style_name?.toLowerCase().includes(lower);
        const matchesStatus = status.toLowerCase().includes(lower);
        const matchesBuyer = sample.styles?.buyer_name?.toLowerCase().includes(lower);
        if (!matchesCode && !matchesName && !matchesStatus && !matchesBuyer) return false;
      }
      return true;
    });
  }, [samples, searchTerm, userProfile?.role, showCompleted]);

  const handleSampleClick = (sampleId: string) => {
    const tab = userProfile?.role === 'cutting_incharge' ? 'cutting'
      : userProfile?.role === 'supervisor' ? 'sewing'
        : 'qc';
    navigate(`/sample/${sampleId}/pieces?tab=${tab}`);
  };

  const toggleSearch = () => {
    if (searchOpen) setSearchTerm('');
    setSearchOpen(!searchOpen);
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col relative">
      {/* ── Header ── */}
      <div className="bg-white/80 backdrop-blur-2xl pt-safe sticky top-0 z-40 px-5 pb-3 pt-4 border-b border-slate-100/80">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center shadow-sm shadow-blue-600/20">
              <span className="text-xs font-bold text-white uppercase">{userProfile?.full_name?.charAt(0)}</span>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none">{userProfile?.role?.replace('_', ' ')}</p>
              <p className="text-slate-800 font-semibold text-sm leading-tight">{userProfile?.full_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleSearch}
              className={clsx(
                "w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200",
                searchOpen ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-400 border border-slate-100"
              )}
            >
              {searchOpen ? <X size={16} /> : <Search size={16} />}
            </button>

            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className={clsx(
                "w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 relative",
                showNotifications ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-400 border border-slate-100"
              )}
            >
              <Bell size={16} />
              {notifications.length > 0 && (
                <span className="absolute top-2 right-2.5 w-1.5 h-1.5 bg-red-500 rounded-full border border-white"></span>
              )}
            </button>

            <button
              onClick={() => navigate('/settings')}
              className={clsx(
                "w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 bg-white text-slate-400 border border-slate-100"
              )}
            >
              <Settings size={16} />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div className="mt-3 animate-slide-down">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
              <input
                type="text" autoFocus
                placeholder="Search style, buyer, status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 pl-9 text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-300 transition-all font-medium"
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Notifications Panel ── */}
      {showNotifications && (
        <div className="fixed inset-0 z-[60] bg-black/20 backdrop-blur-sm" onClick={() => setShowNotifications(false)}>
          <div className="absolute top-20 right-5 w-72 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-slide-down" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Recent Activity</h3>
              <button onClick={() => setShowNotifications(false)}><X size={14} className="text-slate-400" /></button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-xs">No recent updates</div>
              ) : (
                notifications.map(n => (
                  <div key={n.id} onClick={() => { if (n.sampleId) handleSampleClick(n.sampleId); setShowNotifications(false); }} className="px-4 py-3 border-b border-slate-50 hover:bg-blue-50 transition-colors cursor-pointer">
                    <p className="text-xs font-bold text-slate-700 mb-0.5">{n.msg}</p>
                    <p className="text-[10px] text-slate-500 mb-0.5">{n.sub}</p>
                    <p className="text-[10px] text-slate-400 flex items-center gap-1"><Clock size={10} /> {n.time}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Content ── */}
      <main className="flex-1 px-5 pb-24 mt-3 animate-page">
        {/* Section Label & Toggle */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              {searchTerm ? 'Results' : showCompleted ? 'All Jobs' : 'Active Jobs'}
            </h2>
            <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
              {filteredSamples.length}
            </span>
          </div>

          {/* Toggle Completed */}
          {!searchTerm && (
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              className={clsx(
                "flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold transition-all border",
                showCompleted
                  ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                  : "bg-white text-slate-400 border-slate-200"
              )}
            >
              {showCompleted ? <CheckCircle size={10} strokeWidth={3} /> : <Circle size={10} strokeWidth={3} />}
              <span>Show Done</span>
            </button>
          )}
        </div>

        {loading && samples.length === 0 ? (
          // Skeleton loading
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton h-32 w-full rounded-2xl" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-10 text-red-500 text-sm font-medium bg-red-50 rounded-2xl p-4 border border-red-100">{error}</div>
        ) : filteredSamples.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 animate-fade-in">
            <div className="w-16 h-16 bg-slate-50 rounded-2xl mb-4 flex items-center justify-center text-slate-300">
              <Search size={32} />
            </div>
            <p className="text-slate-800 font-bold text-sm">No jobs found</p>
            <p className="text-slate-400 text-xs mt-1">
              {showCompleted ? "No completed jobs either" : "Try toggling 'Show Done'"}
            </p>
          </div>
        ) : (
          <div className="space-y-3 pb-safe">
            {filteredSamples.map((sample, index) => (
              <div
                key={sample.id}
                className="animate-card"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <SampleRequestCard
                  sample={sample}
                  onClick={() => handleSampleClick(sample.id)}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      <BottomDock />
    </div>
  );
};